import React, { useState, useEffect } from 'react';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import Toggle from '../../../Common/Toggle/Toggle';
import '../Nucleos/Nucleos.css';

import API_BASE_URL from '../../../../config/api';

const Diretrizes = () => {
  const [diretrizes, setDiretrizes] = useState([]);
  const [prazosValidacao, setPrazosValidacao] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    diretriz: '',
    id_prazo: '',
    ativo: true
  });

  const getPrazoLabel = (diretriz) => {
    if (diretriz?.prazo_descricao) {
      return diretriz.prazo_descricao;
    }

    if (!diretriz?.id_prazo) {
      return '';
    }

    const prazo = prazosValidacao.find((p) => p.id === diretriz.id_prazo);
    if (!prazo) {
      return '';
    }

    return `${prazo.descricao} (${prazo.validade} dias)`;
  };

  const carregarDiretrizes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/diretrizes`);

      if (!response.ok) {
        throw new Error('Erro ao buscar diretrizes');
      }

      const data = await response.json();
      setDiretrizes(data);
    } catch (error) {
      console.error('Erro ao carregar diretrizes:', error);
      alert(
        'Erro ao carregar diretrizes. Verifique a conexão com o servidor.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDiretrizes();
    
    const fetchPrazosValidacao = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/prazos-validacao`);
        if (!response.ok) {
          throw new Error('Erro ao buscar prazos de validação');
        }
        const data = await response.json();
        setPrazosValidacao(data);
      } catch (error) {
        console.error('Erro ao carregar prazos de validação:', error);
        setPrazosValidacao([]);
      }
    };
    
    fetchPrazosValidacao();
  }, []);

  const filteredDiretrizes = diretrizes.filter((diretriz) =>
    diretriz.diretriz?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.diretriz.trim()) {
      alert('A descrição da diretriz é obrigatória.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        diretriz: formData.diretriz,
        id_prazo: formData.id_prazo ? Number(formData.id_prazo) : null,
        ativo: formData.ativo
      };

      if (editingId) {
        const response = await fetch(
          `${API_BASE_URL}/diretrizes/${editingId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          }
        );

        if (!response.ok) {
          throw new Error('Erro ao atualizar diretriz');
        }

        const atualizada = await response.json();

        setDiretrizes((prev) =>
          prev.map((d) => (d.id === editingId ? atualizada : d))
        );
      } else {
        const response = await fetch(`${API_BASE_URL}/diretrizes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error('Erro ao criar diretriz');
        }

        const criada = await response.json();

        setDiretrizes((prev) => [...prev, criada]);
      }

      resetForm();
    } catch (error) {
      console.error('Erro ao salvar diretriz:', error);
      alert(
        'Erro ao salvar diretriz. Verifique os dados e tente novamente.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (diretriz) => {
    setFormData({
      diretriz: diretriz.diretriz,
      id_prazo: diretriz.id_prazo ? String(diretriz.id_prazo) : '',
      ativo: diretriz.ativo
    });
    setEditingId(diretriz.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        'Tem certeza que deseja excluir esta diretriz?'
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/diretrizes/${id}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok && response.status !== 204) {
        throw new Error('Erro ao excluir diretriz');
      }

      setDiretrizes((prev) => prev.filter((d) => d.id !== id));
    } catch (error) {
      console.error('Erro ao excluir diretriz:', error);
      alert('Erro ao excluir diretriz. Tente novamente.');
    }
  };

  const handleToggleStatus = async (id) => {
    const diretrizSelecionada = diretrizes.find((d) => d.id === id);

    if (!diretrizSelecionada) {
      return;
    }

    const novoAtivo = !diretrizSelecionada.ativo;

    try {
      const response = await fetch(
        `${API_BASE_URL}/diretrizes/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            diretriz: diretrizSelecionada.diretriz,
            id_prazo: diretrizSelecionada.id_prazo ?? null,
            ativo: novoAtivo
          })
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao atualizar status da diretriz');
      }

      const atualizada = await response.json();

      setDiretrizes((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ativo: atualizada.ativo } : d))
      );
    } catch (error) {
      console.error('Erro ao atualizar status da diretriz:', error);
      alert('Erro ao atualizar status da diretriz. Tente novamente.');
    }
  };

  const resetForm = () => {
    setFormData({ diretriz: '', id_prazo: '', ativo: true });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div className="nucleos-container">
      {!showForm ? (
        <>
          <div className="tabela-header">
            <div className="header-content">
              <h1>Diretrizes</h1>
            </div>
            <button 
              className="btn-primary"
              onClick={() => setShowForm(true)}
            >
              <FaPlus /> Nova Diretriz
            </button>
          </div>

          <div className="table-container">
            <div className="table-controls">
              <div className="search-container">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Pesquisar diretrizes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="table-info">
                <span>
                  {loading
                    ? 'Carregando diretrizes...'
                    : `${filteredDiretrizes.length} registro(s) encontrado(s)`}
                </span>
              </div>
            </div>

            <div className="table-wrapper">
              <table className="data-table nucleos-table">
                <thead>
                  <tr>
                    <th className="col-id">ID</th>
                    <th className="col-nome">Diretriz</th>
                    <th className="col-nome" style={{ width: '200px', maxWidth: '200px' }}>Prazos (Validação)</th>
                    <th className="col-status">Status</th>
                    <th className="col-acoes">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDiretrizes.map((diretriz) => (
                    <tr key={diretriz.id}>
                      <td className="col-id">{diretriz.id}</td>
                      <td className="col-nome">{diretriz.diretriz}</td>
                      <td
                        className="col-nome"
                        style={{ width: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={getPrazoLabel(diretriz)}
                      >
                        {getPrazoLabel(diretriz)}
                      </td>
                      <td className="col-status">
                        <div className="status-toggle-wrapper">
                          <Toggle
                            id={`toggle-${diretriz.id}`}
                            checked={diretriz.ativo}
                            onChange={() => handleToggleStatus(diretriz.id)}
                          />
                        </div>
                      </td>
                      <td className="col-acoes">
                        <div className="action-buttons">
                          <button 
                            className="btn-edit"
                            onClick={() => handleEdit(diretriz)}
                            title="Editar"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="btn-delete"
                            onClick={() => handleDelete(diretriz.id)}
                            title="Excluir"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="form-container inline-form">
          <div className="form-header">
            <h2>{editingId ? 'Editar Diretriz' : 'Nova Diretriz'}</h2>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-content">
              <div className="form-group">
                <label htmlFor="diretriz">Diretriz *</label>
                <textarea
                  id="diretriz"
                  value={formData.diretriz}
                  onChange={(e) =>
                    setFormData({ ...formData, diretriz: e.target.value })
                  }
                  required
                  rows={4}
                  maxLength={1000}
                  placeholder="Digite a descrição da diretriz"
                  className="form-textarea"
                />
              </div>

              <div className="form-group">
                <label htmlFor="id_prazo">Prazos (Validação)</label>
                <select
                  id="id_prazo"
                  value={formData.id_prazo}
                  onChange={(e) =>
                    setFormData({ ...formData, id_prazo: e.target.value })
                  }
                >
                  <option value="">Selecione um prazo de validação</option>
                  {prazosValidacao
                    .filter((p) => p.ativo)
                    .map((prazo) => (
                      <option key={prazo.id} value={prazo.id}>
                        {prazo.descricao} ({prazo.validade} dias)
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <Toggle
                  id="ativo-diretriz"
                  label="Status:"
                  checked={formData.ativo}
                  onChange={(e) => setFormData((prev) => ({ ...prev, ativo: e.target.checked }))}
                />
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={resetForm}
              >
                <FaTimes /> Cancelar
              </button>
              <button 
                type="submit" 
                className="btn-primary"
                disabled={saving}
              >
                <FaSave /> {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Salvar')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Diretrizes;
